import { answerForTopic, answerFromKnowledge } from './knowledge.js';

const bubble = document.querySelector('#ariya-speech');
const bubbleText = bubble?.querySelector('p');
const sourceBox = document.querySelector('#speech-sources');
const consoleElement = document.querySelector('#chat-console');
const form = document.querySelector('#ariya-chat-form');
const input = document.querySelector('#ariya-chat-input');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const state = {
  previousTopic: sessionStorage.getItem('aether-ariya-topic') || 'overview',
  request: 0,
};

function renderSources(sources = []) {
  if (!sourceBox) return;
  sourceBox.replaceChildren();
  for (const source of sources) {
    const anchor = document.createElement('a');
    anchor.href = source.url;
    anchor.target = '_blank';
    anchor.rel = 'noreferrer noopener';
    anchor.textContent = source.label;
    sourceBox.append(anchor);
  }
}

function emitSpeaking(active, text = '') {
  window.dispatchEvent(new CustomEvent('ariya:speaking', { detail: { active, text } }));
}

async function typeReply(text, requestId) {
  if (!bubbleText) return;
  if (reducedMotion) {
    bubbleText.textContent = text;
    return;
  }
  bubbleText.textContent = '';
  const words = text.split(' ');
  for (let index = 0; index < words.length; index += 1) {
    if (requestId !== state.request) return;
    bubbleText.textContent += `${index ? ' ' : ''}${words[index]}`;
    await new Promise((resolve) => window.setTimeout(resolve, Math.min(42, 18 + words[index].length * 1.4)));
  }
}

async function presentReply(reply) {
  state.request += 1;
  const requestId = state.request;
  state.previousTopic = reply.topic || state.previousTopic;
  sessionStorage.setItem('aether-ariya-topic', state.previousTopic);
  bubble?.classList.remove('is-thinking');
  bubble?.classList.add('is-speaking');
  renderSources(reply.sources);
  emitSpeaking(true, reply.text);
  await typeReply(reply.text, requestId);
  if (requestId === state.request) {
    window.setTimeout(() => {
      bubble?.classList.remove('is-speaking');
      emitSpeaking(false, reply.text);
    }, 520);
  }
}

async function askPublicConcierge(message) {
  const endpoint = window.AETHER_PUBLIC_CHAT_ENDPOINT;
  if (!endpoint) return answerFromKnowledge(message, state);

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, previousTopic: state.previousTopic }),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Public concierge returned ${response.status}`);
    const payload = await response.json();
    if (typeof payload.answer !== 'string' || !payload.answer.trim()) throw new Error('Public concierge response was empty');
    return { text: payload.answer.trim().slice(0, 900), topic: payload.topic || state.previousTopic, sources: Array.isArray(payload.sources) ? payload.sources : [] };
  } catch (error) {
    console.info('Using the on-site Aether knowledge engine:', error.message);
    return answerFromKnowledge(message, state);
  } finally {
    window.clearTimeout(timeout);
  }
}

export function openChat({ focus = true, scroll = false } = {}) {
  consoleElement?.classList.add('is-open');
  if (scroll) document.querySelector('#lobby')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
  if (focus) window.setTimeout(() => input?.focus(), scroll ? 620 : 40);
}

export async function askAriya(message) {
  const clean = String(message || '').trim().slice(0, 500);
  if (!clean) return;
  openChat({ focus: false });
  bubble?.classList.add('is-thinking');
  if (bubbleText) bubbleText.textContent = 'Let me check Aether’s public knowledge…';
  renderSources([]);
  emitSpeaking(false);
  await new Promise((resolve) => window.setTimeout(resolve, reducedMotion ? 0 : 260));
  const reply = await askPublicConcierge(clean);
  await presentReply(reply);
}

form?.addEventListener('submit', (event) => {
  event.preventDefault();
  const message = input.value;
  input.value = '';
  askAriya(message);
});

document.querySelectorAll('[data-open-chat]').forEach((control) => {
  control.addEventListener('click', () => openChat({ focus: true, scroll: !control.closest('#lobby') }));
});

document.querySelectorAll('[data-prompt]').forEach((control) => {
  control.addEventListener('click', () => {
    openChat({ focus: false, scroll: !control.closest('#lobby') });
    window.setTimeout(() => askAriya(control.dataset.prompt), control.closest('#lobby') ? 0 : 600);
  });
});

document.querySelectorAll('[data-topic]').forEach((control) => {
  control.addEventListener('click', () => {
    document.querySelectorAll('[data-topic]').forEach((item) => item.classList.toggle('is-active', item === control));
    presentReply(answerForTopic(control.dataset.topic));
    openChat({ focus: false });
  });
});

document.querySelectorAll('[data-chat-topic]').forEach((card) => {
  card.querySelector('button')?.addEventListener('click', () => {
    openChat({ focus: false, scroll: true });
    window.setTimeout(() => presentReply(answerForTopic(card.dataset.chatTopic)), 620);
  });
});

input?.addEventListener('focus', () => consoleElement?.classList.add('is-open'));

export function initializeGreeting() {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  if (bubbleText) bubbleText.textContent = `${salutation}, and welcome to Aether Systems. Select a division from the blue menu for a briefing, or ask me about the product or service you need.`;
}
